import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

interface User {
  username: string;
  role: string;
  initials: string;
}

interface WalletInfo {
  address: string;
  network: string;
  chainId: number;
}

const MOCK_NETWORKS = [
  { network: 'Ethereum Mainnet', chainId: 1 },
  { network: 'Polygon', chainId: 137 },
  { network: 'Arbitrum One', chainId: 42161 },
  { network: 'Base', chainId: 8453 },
];

interface AuthState {
  user: User | null;
  wallet: WalletInfo | null;
  isAuthenticated: boolean;
  login: (username: string) => Promise<boolean>;
  loginWithSSO: (provider: 'okta' | 'google' | 'microsoft') => Promise<boolean>;
  logout: () => void;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

function generateMockAddress(): string {
  const hex = Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `0x${hex}`;
}

function getInitials(username: string): string {
  return username
    .split(/[\s._-]+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const stored = sessionStorage.getItem('ascend_user');
    return stored ? (JSON.parse(stored) as User) : null;
  });

  const [wallet, setWallet] = useState<WalletInfo | null>(() => {
    const stored = sessionStorage.getItem('ascend_wallet');
    return stored ? (JSON.parse(stored) as WalletInfo) : null;
  });

  const login = useCallback(async (username: string): Promise<boolean> => {
    await new Promise((r) => setTimeout(r, 400));
    const u: User = {
      username,
      role: 'Administrator',
      initials: getInitials(username) || 'AD',
    };
    setUser(u);
    sessionStorage.setItem('ascend_user', JSON.stringify(u));
    return true;
  }, []);

  const loginWithSSO = useCallback(async (provider: 'okta' | 'google' | 'microsoft'): Promise<boolean> => {
    await new Promise((r) => setTimeout(r, 800));
    const names: Record<string, string> = { okta: 'admin@ascend.io', google: 'admin@ascend.io', microsoft: 'admin@ascend.onmicrosoft.com' };
    const u: User = {
      username: names[provider] ?? 'sso-user',
      role: 'Administrator',
      initials: 'AD',
    };
    setUser(u);
    sessionStorage.setItem('ascend_user', JSON.stringify(u));
    return true;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setWallet(null);
    sessionStorage.removeItem('ascend_user');
    sessionStorage.removeItem('ascend_wallet');
  }, []);

  const connectWallet = useCallback(async () => {
    await new Promise((r) => setTimeout(r, 600));
    const addr = generateMockAddress();
    const net = MOCK_NETWORKS[Math.floor(Math.random() * MOCK_NETWORKS.length)];
    const w: WalletInfo = { address: addr, network: net.network, chainId: net.chainId };
    setWallet(w);
    sessionStorage.setItem('ascend_wallet', JSON.stringify(w));
  }, []);

  const disconnectWallet = useCallback(() => {
    setWallet(null);
    sessionStorage.removeItem('ascend_wallet');
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      wallet,
      isAuthenticated: !!user,
      login,
      loginWithSSO,
      logout,
      connectWallet,
      disconnectWallet,
    }),
    [user, wallet, login, loginWithSSO, logout, connectWallet, disconnectWallet],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
